import { type ComponentProps, useEffect, useRef, useState } from "react";
import {
  Box,
  CommentIcon,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Row,
  Tooltip,
  Text,
} from "@hightouch/htui";

import {
  isResolvedAssetCommentThreadStatus,
  type CommentThreadSubject,
  type CommentAnchor,
} from "@hightouch/lib/llm/asset-comments/types";
import { useCommentLayer } from "src/components/comments/comment-keys";
import { useCloseOnPressOutside } from "src/components/comments/use-close-on-press-outside";
import { CommentsPanel } from "src/components/comments/comments-panel";
import { useCommentThreads } from "src/components/comments/use-comment-threads";
import {
  DEFAULT_COMMENT_FILTERS,
  filterThreadsToVersion,
} from "src/components/comments/utils";
import { FollowTrigger } from "src/components/comments/follow-trigger";
import type { AssetCommentThreadsQuery } from "src/graphql";
import { NewFileComment } from "src/components/comments/new-file-comment";
import { InlineCommentThread } from "src/components/comments/inline-comment-thread";
import { PinAvatar, PinCount } from "src/components/comments/comment-pin";
import { PIN_SHAPE, PIN_SIZE, UNREAD_PIN } from "src/components/comments/pin";

type Props = {
  subject: CommentThreadSubject;
  /** Changing the key discards the current draft. */
  composerKey?: string;
  newCommentAnchor?: CommentAnchor | null;
  newCommentLabel?: string | null;
  threadSubtitle?: (thread: { anchor?: unknown }) => string | null;
  onSelectThread?: (threadId: string) => void;
  brandProfileId?: string | null;
  versionCommentCutoff?: string | null;
  canCreate?: boolean;
  /** Controlled, so the tile's menu or a doc's comment list can open it. */
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  /** What to show as it opens: a thread, or the list for null. A new object per request, so repeating one reopens it. */
  openRequest?: { threadId: string | null } | null;
  /** Narrows the threads it counts and lists, like a doc's whole-doc comments. */
  threadFilter?: (thread: { anchor?: unknown }) => boolean;
  /** Shows a button to add the first comment rather than nothing. */
  showWhenEmpty?: boolean;
  /** The file's threads, when a parent already loads them (a canvas loads every tile's at once). */
  threads?: AssetCommentThreadsQuery["asset_comment_threads"];
  /** "fixed" when the stack follows something that pans or scrolls, so its popover can't make the page scroll. */
  strategy?: ComponentProps<typeof Popover>["strategy"];
  /** Called once a close finishes and the popover's content is gone, so focus has left it. */
  onCloseComplete?: () => void;
};

/**
 * Every open comment on a file, as one stack: its author's initials for a
 * single thread, or a count, filled while any is unread. Opens the comments
 * list, where a thread opens in place, with a composer for a new thread.
 */
export function FileCommentStack({
  subject,
  brandProfileId,
  versionCommentCutoff = null,
  canCreate = true,
  isOpen,
  onOpenChange,
  openRequest = null,
  threadFilter,
  showWhenEmpty = false,
  threads: loadedThreads,
  strategy,
  newCommentAnchor = null,
  composerKey,
  newCommentLabel,
  threadSubtitle,
  onSelectThread,
  onCloseComplete,
}: Props) {
  const { threads, toComments, isUnread } = useCommentThreads({
    subject,
    threads: loadedThreads,
  });
  const [filters, setFilters] = useState(DEFAULT_COMMENT_FILTERS);
  const [threadId, setThreadId] = useState<string | null>(null);
  // Each opening starts from the list, or the thread it was opened on.
  useEffect(() => {
    if (isOpen) setThreadId(openRequest?.threadId ?? null);
  }, [isOpen, openRequest]);
  const threadsInVersion = filterThreadsToVersion(
    threads ?? [],
    versionCommentCutoff,
  );
  const visibleThreadId = threadsInVersion.some(
    (thread) => thread.id === threadId,
  )
    ? threadId
    : null;
  const close = () => onOpenChange(false);
  // Escape steps back from a thread to the list, then closes.
  useCommentLayer(
    isOpen ? () => (visibleThreadId ? setThreadId(null) : close()) : null,
  );
  const pressInside = useCloseOnPressOutside(isOpen ? close : null);
  const open = threadsInVersion.filter(
    (thread) =>
      !isResolvedAssetCommentThreadStatus(thread.current_status) &&
      (threadFilter?.(thread) ?? true),
  );
  const authors = [
    ...new Set(
      open.flatMap((thread) => toComments(thread)[0]?.author.name ?? []),
    ),
  ];
  const unread = open.filter(isUnread).length;
  const label = describeStack(open.length, authors.length, unread);
  const isHidden = open.length === 0 && !isOpen && !showWhenEmpty;
  const onExitComplete = useCloseComplete({
    isOpen,
    isHidden,
    onCloseComplete,
  });

  if (isHidden) return null;

  return (
    <Popover
      isOpen={isOpen}
      onClose={close}
      placement="right-start"
      // A press outside closes it instead, since its menus portal out of it.
      closeOnBlur={false}
      isLazy
      strategy={strategy}
    >
      {({ forceUpdate }) => (
        <>
          {isOpen && <FollowTrigger onMove={forceUpdate} />}
          <PopoverTrigger>
            <Box display="inline-flex" {...pressInside}>
              <Tooltip message={label}>
                <Row
                  as="button"
                  type="button"
                  aria-label={label}
                  {...PIN_SHAPE}
                  {...(unread > 0 && { bg: UNREAD_PIN.bg })}
                  // Open, it carries the focus ring a thread's pin does when its card is up.
                  boxShadow={isOpen ? "outline" : PIN_SHAPE.boxShadow}
                  height={`${PIN_SIZE}px`}
                  minWidth={`${PIN_SIZE}px`}
                  // Square like a thread's pin, until a count needs the room.
                  px={open.length > 9 ? 1 : 0}
                  gap={0.5}
                  align="center"
                  justify="center"
                  color={unread > 0 ? "white" : "teal.700"}
                  // Nothing to show yet: the menu opened it, so it only anchors the popover.
                  visibility={
                    open.length === 0 && !showWhenEmpty ? "hidden" : undefined
                  }
                  _hover={{ bg: unread > 0 ? UNREAD_PIN.hover : "teal.200" }}
                  onClick={(event) => {
                    event.stopPropagation();
                    onOpenChange(!isOpen);
                  }}
                >
                  {open.length === 0 ? (
                    <Box as={CommentIcon} boxSize={4} />
                  ) : // Drawn like a thread's pin, so the two read the same side by side.
                  open.length === 1 && authors[0] ? (
                    <PinAvatar name={authors[0]} isUnread={unread > 0} />
                  ) : (
                    <PinCount count={open.length} isUnread={unread > 0} />
                  )}
                </Row>
              </Tooltip>
            </Box>
          </PopoverTrigger>
          {/* Portaled out of the canvas, whose zoom would otherwise scale it. */}
          <Portal>
            <PopoverContent
              width="xs"
              overflow="hidden"
              onClick={(event) => event.stopPropagation()}
              // A prop, not in motionProps: PopoverContent replaces a motionProps handler with its own.
              onAnimationComplete={onCloseComplete && onExitComplete}
              {...pressInside}
            >
              <PopoverBody p={0}>
                <Box height={72}>
                  {visibleThreadId ? (
                    // Loads its file's threads itself, so an agent's reply polls in fast while it is open.
                    <InlineCommentThread
                      subject={subject}
                      brandProfileId={brandProfileId}
                      threadId={visibleThreadId}
                      subtitle={threadSubtitle?.(
                        threadsInVersion.find(
                          (thread) => thread.id === visibleThreadId,
                        ) ?? {},
                      )}
                      onBack={() => setThreadId(null)}
                      onDismiss={() => setThreadId(null)}
                    />
                  ) : (
                    <CommentsPanel
                      subject={subject}
                      threads={loadedThreads}
                      brandProfileId={brandProfileId}
                      selectedThreadId={null}
                      filters={filters}
                      onFiltersChange={setFilters}
                      emptyMessage="No comments yet"
                      onSelectThread={(id) => {
                        setThreadId(id);
                        if (id) onSelectThread?.(id);
                      }}
                      threadSubtitle={threadSubtitle}
                      onClose={close}
                      threadFilter={threadFilter}
                      versionCommentCutoff={versionCommentCutoff}
                    />
                  )}
                </Box>
                {!visibleThreadId && canCreate && (
                  <Box
                    p={3}
                    borderTopWidth="hairline"
                    borderColor="base.divider"
                  >
                    {newCommentLabel && (
                      <Text size="sm" color="text.secondary">
                        {newCommentLabel}
                      </Text>
                    )}
                    <NewFileComment
                      key={composerKey}
                      newCommentAnchor={newCommentAnchor}
                      subject={subject}
                      brandProfileId={brandProfileId}
                    />
                  </Box>
                )}
              </PopoverBody>
            </PopoverContent>
          </Portal>
        </>
      )}
    </Popover>
  );
}

/** Calls `onCloseComplete` after the exit plays and the content unmounts, or as the stack hides, which plays no exit. */
function useCloseComplete({
  isOpen,
  isHidden,
  onCloseComplete,
}: {
  isOpen: boolean;
  isHidden: boolean;
  onCloseComplete?: () => void;
}) {
  const callback = useRef(onCloseComplete);
  callback.current = onCloseComplete;
  const phase = useRef<"closed" | "open" | "closing">("closed");
  const [hasExited, setHasExited] = useState(false);
  // Runs after the render that unmounts the content: the hide's own, or the one the exit's completion triggers.
  useEffect(() => {
    if (isOpen) {
      phase.current = "open";
      // An exit that finishes as it reopens must not end the next close early.
      setHasExited(false);
      return;
    }
    if (phase.current === "open") phase.current = "closing";
    if (phase.current !== "closing" || !(hasExited || isHidden)) return;
    phase.current = "closed";
    setHasExited(false);
    callback.current?.();
  }, [isOpen, isHidden, hasExited]);
  return (definition: unknown) => {
    // The exit also completes as the popover mounts closed, sometimes after it opens.
    if (definition === "exit" && phase.current === "closing")
      setHasExited(true);
  };
}

/** What the stack holds, for its label and tooltip: "3 comments from 2 people, 1 unread". */
function describeStack(
  comments: number,
  people: number,
  unread: number,
): string {
  if (comments === 0) return "Add a comment";
  const parts = [
    `${comments} ${comments === 1 ? "comment" : "comments"}`,
    people > 1 ? `from ${people} people` : null,
  ];
  const summary = parts.filter(Boolean).join(" ");
  return unread > 0 ? `${summary}, ${unread} unread` : summary;
}
